import { FC, useEffect } from 'react';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useFetchTransactionsHistory from '@/hooks/useApi/useFetchTransactionsHistory';
import {
  Box,
  formattedCurrency,
  EftTransactionResponse,
  LinkedTransactionsMap,
  withStyles,
  Card,
  capitalizeFirstLetter,
} from 'vopay';
import { TransactionCard, Title, DetailsRows, TransactionStatus } from 'UI';
import styles from '../Transactions.module.scss';

const cn = withStyles(styles);

type TransactionInfoProps = {
  transaction: EftTransactionResponse & {
    ParentTransaction?: LinkedTransactionsMap;
    SubTransactions?: LinkedTransactionsMap;
  };
  isParent: boolean;
};

type DataProps = {
  transactionId: string;
  hasErrorInfo: boolean;
  failureReason: string;
};

const RefundTransactionInfo: FC<TransactionInfoProps> = ({ transaction, isParent = false }) => {
  const { state } = useLocation<DataProps>();
  const currentDate = moment().utc().format('YYYY-MM-DD');
  const { transactions, fetchTransactionsHistory } = useFetchTransactionsHistory();

  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.RefundTransactionInfo',
  });
  const { t: tGlobal } = useTranslation('translations');

  useEffect(() => {
    fetchTransactionsHistory({
      StartDateTime: moment().subtract(5, 'years').format('YYYY-MM-DD'),
      EndDateTime: currentDate,
      TransactionID: transaction.TransactionID,
    });
  }, [transaction.TransactionID, currentDate]);

  const transactionDetails = [
    {
      label: t('TransactionDetails.transactionStatus', { defaultValue: 'Transaction Status' }),
      value: (
        <Box jc="fe">
          <TransactionStatus
            status={transaction.TransactionStatus}
            label={capitalizeFirstLetter(
              tGlobal(`Transaction.TransactionHistory.statuses.${transaction.TransactionStatus}`, {
                defaultValue: transaction.TransactionStatus,
              }),
            )}
          />
        </Box>
      ),
      visible: isParent,
    },
    {
      label: t('TransactionDetails.transactionDate', { defaultValue: 'Transaction Date' }),
      value: transaction.TransactionDateTime,
    },
    {
      label: t('TransactionDetails.lastModified', { defaultValue: 'Last Modified' }),
      value: transaction.LastModified,
    },
    {
      label: t('TransactionDetails.type', { defaultValue: 'Transaction Type' }),
      value: tGlobal('Transaction.TransactionHistory.transactionTypes.refund', { defaultValue: 'Refund' }),
    },
    {
      label: t('TransactionDetails.holdAmount', { defaultValue: 'Hold Amount' }),
      value: transaction.HoldAmount ? `${transaction.Currency} ${formattedCurrency(transaction.HoldAmount)}` : '-',
    },
    {
      label: t('TransactionDetails.clientReferenceNumber', { defaultValue: 'Client Reference Number' }),
      value: transaction.ClientReferenceNumber || '-',
    },
    {
      label: t('TransactionDetails.notes', { defaultValue: 'Notes' }),
      value: transactions.map((data) => data.Notes).pop() || '-',
    },
  ];

  const recipientDetails = [
    {
      label: t('AccountHolderDetails.address', { defaultValue: 'Address' }),
      value: transaction.Address1 || transaction.Address2 || '-',
    },
    {
      label: t('AccountHolderDetails.city', { defaultValue: 'City' }),
      value: transaction.City || '-',
    },
    {
      label: t('AccountHolderDetails.postalCode', { defaultValue: 'PostalCode / Zipcode' }),
      value: transaction.PostalCode || '-',
    },
    {
      label: t('AccountHolderDetails.country', { defaultValue: 'Country' }),
      value: transaction.Country || '-',
    },
    {
      label: t('AccountHolderDetails.primaryContact', { defaultValue: 'Primary Contact' }),
      value: transaction.PhoneNumber || '-',
    },
  ];

  const bankAccountDetails = [
    {
      label: t('BankAccountDetails.financialInstitutionNumber', { defaultValue: 'Financial Institution Number' }),
      value: transaction.FinancialInstitutionNumber || '-',
    },
    {
      label: t('BankAccountDetails.branchTransitNumber', { defaultValue: 'Branch Transit Number' }),
      value: transaction.BranchTransitNumber?.replaceAll('*', '•') || '-',
    },
    {
      label: t('BankAccountDetails.accountNumber', { defaultValue: 'Account Number' }),
      value: transaction.AccountNumber?.replaceAll('*', '•') || '-',
    },
  ];

  const errorDetails = [
    {
      label: t('ErrorInfo.failureDateTime', { defaultValue: 'Failure Date Time' }),
      value: transaction.LastModified,
    },
    {
      label: t('ErrorInfo.amount', { defaultValue: 'Amount' }),
      value: `${transaction.Currency} ${formattedCurrency(transaction.Amount)}`,
    },
    {
      label: t('ErrorInfo.failureReason', { defaultValue: 'Failure Reason' }),
      value: state.failureReason || '-',
    },
  ];

  return (
    <Box flx="col" gapSize="24px">
      {!isParent && (
        <TransactionCard
          receiverName={transaction.FirstName + ' ' + transaction.LastName}
          amount={transaction.Amount}
          currency={transaction.Currency}
          transactionId={transaction.TransactionID}
          status={transaction.TransactionStatus}
          type={'Refund'}
          transactionDate={transaction.TransactionDateTime}
          lastModified={transaction.LastModified}
          failureReason={transaction.FailureReason}
        />
      )}

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('transactionDetailsHeader', { defaultValue: 'Transaction Details' })}
        </Title>
        <DetailsRows className={cn('rows')} details={transactionDetails} />
      </Card>

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('recipientInformationHeader', { defaultValue: 'Recipient Information' })}
        </Title>
        <DetailsRows className={cn('rows')} details={recipientDetails} />
      </Card>

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('bankAccountDetailsHeader', { defaultValue: 'Bank Account Details' })}
        </Title>
        <DetailsRows className={cn('rows')} details={bankAccountDetails} />
      </Card>

      {state?.hasErrorInfo && (
        <Card className={cn('card')}>
          <Title level="4" className={cn('title')}>
            {t('errorInfoHeader', { defaultValue: 'Error Info' })}
          </Title>
          <DetailsRows className={cn('rows')} details={errorDetails} />
        </Card>
      )}
    </Box>
  );
};

export default RefundTransactionInfo;
